import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const btnPrimary =
  "inline-flex items-center justify-center gap-2 rounded-full bg-[#5B2C91] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(91,44,145,0.22)] hover:bg-[#4A2277] transition-colors disabled:opacity-50 disabled:pointer-events-none";
export const btnSecondary =
  "inline-flex items-center justify-center gap-2 rounded-full bg-[#5B2C91]/8 px-4 py-2 text-sm font-semibold text-[#5B2C91] hover:bg-[#5B2C91]/15 transition-colors disabled:opacity-50 disabled:pointer-events-none";
export const btnGhost =
  "inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-[#4A4059] hover:bg-[#F6F4FA] hover:text-[#5B2C91] transition-colors disabled:opacity-50 disabled:pointer-events-none";
export const btnDanger =
  "inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:pointer-events-none";
export const inputCls =
  "w-full rounded-2xl border border-[#5B2C91]/15 bg-white px-4 py-2.5 text-sm text-[#1F152E] placeholder:text-[#8A809A] focus:outline-none focus:ring-2 focus:ring-[#5B2C91]/30 focus:border-[#5B2C91]/40 disabled:bg-[#F6F4FA]";

export const STATUS = {
  pending: { label: "In attesa", cls: "bg-amber-100 text-amber-800" },
  approved: { label: "Approvato", cls: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Rifiutato", cls: "bg-red-100 text-red-700" },
  suspended: { label: "Sospeso", cls: "bg-slate-200 text-slate-700" },
  requested: { label: "Richiesta", cls: "bg-amber-100 text-amber-800" },
  confirmed: { label: "Confermata", cls: "bg-[#5B2C91]/10 text-[#5B2C91]" },
  completed: { label: "Completata", cls: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Annullata", cls: "bg-slate-200 text-slate-600" },
};

export const VISIT_STATUSES = ["requested", "confirmed", "completed", "cancelled"];

export const fmtDate = (iso, pattern = "EEE dd/MM/yyyy") => {
  if (!iso) return "—";
  try {
    return format(parseISO(iso), pattern, { locale: it });
  } catch {
    return iso;
  }
};

export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return format(new Date(iso), "dd/MM/yyyy HH:mm", { locale: it });
  } catch {
    return iso;
  }
};

export function Spinner({ className = "" }) {
  return <Loader2 className={`h-6 w-6 animate-spin text-[#5B2C91] ${className}`} data-testid="spinner" />;
}

export function Panel({ className = "", children, ...props }) {
  return (
    <div
      className={`rounded-3xl bg-white p-5 sm:p-6 ring-1 ring-[#5B2C91]/8 shadow-[0_8px_30px_rgb(91,44,145,0.05)] ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

export function PageHeader({ eyebrow, title, desc, actions, testid = "page-header" }) {
  return (
    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-testid={testid}>
      <div>
        {eyebrow && (
          <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#5B2C91] ring-1 ring-[#5B2C91]/10">
            <span className="h-1.5 w-1.5 rounded-full bg-[#D4F84A]" />
            {eyebrow}
          </span>
        )}
        <h1 className="font-heading mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-[#1F152E]">{title}</h1>
        {desc && <p className="mt-2 max-w-2xl text-sm sm:text-base text-[#6B6280]">{desc}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, hint, icon: Icon, color = "#5B2C91", testid }) {
  return (
    <Panel className="flex items-start gap-4" data-testid={testid}>
      {Icon && (
        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl" style={{ backgroundColor: `${color}18`, color }}>
          <Icon className="h-5 w-5" />
        </span>
      )}
      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#8A809A]">{label}</p>
        <p className="font-heading mt-1 text-3xl font-semibold text-[#1F152E] leading-none">{value}</p>
        {hint && <p className="mt-1.5 text-xs text-[#6B6280]">{hint}</p>}
      </div>
    </Panel>
  );
}

export function StatusBadge({ status, testid }) {
  const s = STATUS[status] || { label: status, cls: "bg-slate-100 text-slate-700" };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.cls}`} data-testid={testid}>
      {s.label}
    </span>
  );
}

export function Field({ label, hint, children, htmlFor, className = "" }) {
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-[#6B6280]">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-[#8A809A]">{hint}</p>}
    </div>
  );
}

export function EmptyState({ title, desc, testid = "empty-state", children }) {
  return (
    <div className="rounded-3xl border border-dashed border-[#5B2C91]/20 bg-white/60 px-6 py-12 text-center" data-testid={testid}>
      <p className="font-heading text-lg font-semibold text-[#1F152E]">{title}</p>
      {desc && <p className="mt-1 text-sm text-[#6B6280]">{desc}</p>}
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}

export function ErrorText({ children, testid = "form-error" }) {
  if (!children) return null;
  return (
    <p className="rounded-2xl bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700" data-testid={testid}>
      {children}
    </p>
  );
}

export function ConfirmDialog({ open, onOpenChange, title, desc, onConfirm, confirmLabel = "Conferma", destructive = false }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-3xl" data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading">{title}</AlertDialogTitle>
          {desc && <AlertDialogDescription>{desc}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid="confirm-cancel">Annulla</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className={`rounded-full ${destructive ? "bg-red-600 hover:bg-red-700" : "bg-[#5B2C91] hover:bg-[#4A2277]"}`}
            data-testid="confirm-ok"
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
